import { memo, useMemo } from 'react'
import { FC } from 'react'
import * as styles from 'components/features/socialFeed/FeedAuthorPage/FeedAuthorPage.styl'
import { FeedInternalLayout, FeedExternalLayout } from 'components/layouts/Layouts'
import { PageHeading } from 'components/atoms/Text'
import FeedPostListContainer from 'components/features/socialFeed/FeedPostList/FeedPostListContainer'
import TagSvg from 'icons/tag.svg'
import SvgIcon from 'components/atoms/SvgIcon'
import { Anchor as AnchorType } from 'interfaces/navigation'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { createFeedTagFromURLSelector, createIsCurrentlySelectedTagLoaded } from 'selectors/feedSelectors'
import { FeedTagResource } from 'interfaces/feed'
import { getFeedTagActionCreator } from 'actions/feedActions'
import { SimpleGuard } from 'interfaces/guards'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import PageLoadingOverlay from 'components/atoms/preloaders/PageLoadingOverlay'
import { isAuthenticatedSelector } from 'selectors/globalSelectors'
import { ResourceFollowButton } from 'components/features/pfm/ResourceFollowButton/ResourceFollowButton'
import { viewportSegmentSelector } from 'selectors/viewportSelectors'
import { PageMeta } from 'components/features/pfm/PageMeta/PageMeta'


const feedTagGuard: SimpleGuard<FeedTagResource> = {
  name: `Feed Tag Page Container`,
  checker: createIsCurrentlySelectedTagLoaded(),
  loader: (dispatch, { name }) => {
    if (!name) {
      return
    }

    dispatch(getFeedTagActionCreator.request({ tagName: name }))
  },
}

export type FeedTagPageInnerProps = FeedTagResource

export const FeedTagPageInner: FC<FeedTagPageInnerProps> = memo((props) => {
  const { name } = props

  const feedTagFromURLSelector = useMemo(createFeedTagFromURLSelector, [])
  const tag = useStoreSelector((state) => feedTagFromURLSelector(state, props))
  const isAuthenticated = useStoreSelector(isAuthenticatedSelector)

  const isFeedTagLoaded = useSimpleGuard(feedTagGuard, props, 'FeedTagPage')

  const tagAnchor = useMemo<AnchorType>(() => ({
    anchor: 'feedtag',
    id: name,
  }), [name])

  const viewportSegment = useStoreSelector(viewportSegmentSelector)
  const followButtonMode = useMemo(() => {
    if (viewportSegment === 'palm-mobile') {
      return 'large'
    }

    if (viewportSegment === 'mobile') {
      return 'medium'
    }

    return 'small'
  }, [viewportSegment])

  const contentChild = useMemo(() => (
    <>
      <PageMeta
        title={ `#${ name } posts on Status Money` }
        description={ `See the latest posts about #${ name } on Status Money. Join the conversation.` }
      />
      <section className={ styles.column }>
        <header className={ styles.header }>
          <div className={ styles.tagAvatar }>
            <div className={ styles.iconWrapper }>
              <SvgIcon src={ TagSvg } />
            </div>
          </div>
          <div className={ styles.resourceName }>
            <PageHeading className={ styles.pageHeading }>#{ name }</PageHeading>
          </div>
          <div className={ styles.followButtonWrapper }>
            <ResourceFollowButton
              type='tag'
              name={ name }
              mode={ followButtonMode }
            />
          </div>
        </header>
        <FeedPostListContainer
          resource={{
            type: 'tag',
            name,
          }}
        />
      </section>
    </>
  ), [followButtonMode, name])


  if (!isFeedTagLoaded || !tag) {
    return (
      <PageLoadingOverlay />
    )
  }

  if (isAuthenticated) {
    return (
      <FeedInternalLayout anchor={ tagAnchor } >
        { contentChild }
      </FeedInternalLayout>
    )
  }

  return (
    <FeedExternalLayout>
      { contentChild }
    </FeedExternalLayout>
  )
})
